import { Controller } from "@hotwired/stimulus"

type TurnstileTheme = "light" | "dark" | "auto"

type TurnstileRenderOptions = {
  sitekey: string
  theme?: TurnstileTheme
}

type TurnstileInstance = {
  ready: (callback: () => void) => void
  render: (container: Element, options: TurnstileRenderOptions) => string
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileInstance
  }
}

export default class TurnstileController extends Controller {
  private static readonly POLLING_INTERVAL_MS = 200
  private static readonly POLLING_TIMEOUT_MS = 30_000

  declare element: HTMLElement

  private widgetId: string | null = null
  private pollingId: number | null = null
  private pollingStartedAt: number | null = null

  connect() {
    this.renderTurnstile()
  }

  disconnect() {
    this.stopPolling()

    const turnstile = window.turnstile
    if (this.widgetId && turnstile) {
      turnstile.remove(this.widgetId)
      this.widgetId = null
    }
  }

  private renderTurnstile() {
    const turnstile = window.turnstile
    if (!turnstile) {
      this.startPolling()
      return
    }

    turnstile.ready(() => {
      if (this.widgetId || !this.element.isConnected) {
        return
      }

      const sitekey = this.element.dataset.sitekey
      if (!sitekey) {
        return
      }

      this.resetContainer()

      const theme = this.element.dataset.theme as TurnstileTheme | undefined
      const options: TurnstileRenderOptions = { sitekey }
      if (theme) {
        options.theme = theme
      }

      this.widgetId = turnstile.render(this.element, options)
    })
  }

  private startPolling() {
    if (this.pollingId !== null) {
      return
    }

    this.pollingStartedAt = Date.now()

    this.pollingId = window.setInterval(() => {
      if (!window.turnstile) {
        const elapsed = Date.now() - (this.pollingStartedAt ?? Date.now())
        if (elapsed >= TurnstileController.POLLING_TIMEOUT_MS) {
          this.stopPolling()
          console.warn("[Turnstile] api.js did not load within polling timeout")
        }
        return
      }

      this.stopPolling()
      this.renderTurnstile()
    }, TurnstileController.POLLING_INTERVAL_MS)
  }

  private stopPolling() {
    if (this.pollingId !== null) {
      window.clearInterval(this.pollingId)
      this.pollingId = null
    }

    this.pollingStartedAt = null
  }

  private resetContainer() {
    this.element.innerHTML = ""

    const form = this.element.closest("form")
    form?.querySelectorAll('input[name="cf-turnstile-response"]').forEach((responseField) => {
      responseField.remove()
    })
  }
}
